/**
 * Linear domino chain. Each placement records which pip ended up on the
 * left side of the placement vs the right — so the chain's open ends are
 * just `first.leftPip` and `last.rightPip`.
 *
 * Doubles play with both pips equal; visually the renderer rotates them
 * 90° as a cue, but matching is purely by pip value either way.
 *
 * The chain is immutable — playLeft/playRight return a new chain.
 */

import type { Domino } from './domino.js';
import { isDouble, otherHalf } from './domino.js';

export interface Placement {
  domino: Domino;
  /** Pip showing on the left side of THIS placement (matters for ends). */
  leftPip: number;
  /** Pip showing on the right side of THIS placement. */
  rightPip: number;
  /** True if this tile is a double (renderer rotates 90°). */
  double: boolean;
  /** Which side it was played on. The opening tile is 'open'. */
  side: 'left' | 'right' | 'open';
}

export interface Chain {
  placements: Placement[];
}

export const emptyChain: Chain = { placements: [] };

export function chainLeftEnd(chain: Chain): number | null {
  if (chain.placements.length === 0) return null;
  return chain.placements[0]!.leftPip;
}

export function chainRightEnd(chain: Chain): number | null {
  if (chain.placements.length === 0) return null;
  return chain.placements[chain.placements.length - 1]!.rightPip;
}

export function chainContains(chain: Chain, dominoId: string): boolean {
  return chain.placements.some((p) => p.domino.id === dominoId);
}

export function canPlayLeft(chain: Chain, d: Domino): boolean {
  const end = chainLeftEnd(chain);
  if (end === null) return true;
  return d.low === end || d.high === end;
}

export function canPlayRight(chain: Chain, d: Domino): boolean {
  const end = chainRightEnd(chain);
  if (end === null) return true;
  return d.low === end || d.high === end;
}

/** Place the opening domino. The first tile typically has its higher pip
 *  on the right just for a deterministic visual; doubles look the same
 *  either way. */
export function openWith(d: Domino): Chain {
  return {
    placements: [{
      domino: d,
      leftPip: d.low,
      rightPip: d.high,
      double: isDouble(d),
      side: 'open',
    }],
  };
}

export function playLeft(chain: Chain, d: Domino): Chain {
  const end = chainLeftEnd(chain);
  if (end === null) return openWith(d);
  if (d.low !== end && d.high !== end) {
    throw new Error(`domino ${d.id} cannot be played on left end ${end}`);
  }
  // Matched pip ends up on the RIGHT side of the new placement (touching
  // the existing chain). The "other" half becomes the new outer left end.
  const outer = otherHalf(d, end);
  const placement: Placement = {
    domino: d,
    leftPip: outer,
    rightPip: end,
    double: isDouble(d),
    side: 'left',
  };
  return { placements: [placement, ...chain.placements] };
}

export function playRight(chain: Chain, d: Domino): Chain {
  const end = chainRightEnd(chain);
  if (end === null) return openWith(d);
  if (d.low !== end && d.high !== end) {
    throw new Error(`domino ${d.id} cannot be played on right end ${end}`);
  }
  // Matched pip on the LEFT (touching), other half becomes new outer right.
  const outer = otherHalf(d, end);
  const placement: Placement = {
    domino: d,
    leftPip: end,
    rightPip: outer,
    double: isDouble(d),
    side: 'right',
  };
  return { placements: [...chain.placements, placement] };
}
